import * as d3 from "d3";
import { useEffect, useRef } from "react";

interface Props {
  id: string;
  data: number[];
  labels: {
    x: string,
    y: string
  }
  legend: {
    names: string[];
  }
  x_max: number;
  x_min: number;
  color: string;
}

const original_width = 1000;
const original_height = 500;

const margin = { top: 10, right: 0, bottom: 40, left: 50 };
const width = original_width - margin.left - margin.right;
const height = original_height - margin.top - margin.bottom;

export default function Chart(props: Props) {

  const axisBottomRef = useRef<SVGGElement>(null);
  const axisLeftRef = useRef<SVGGElement>(null);
  const chartCanvas = useRef<SVGGElement>(null);

  useEffect(() => {

    if (props.data === undefined) return;


    let bins = d3.bin()
      .domain([props.x_min, props.x_max])
      .thresholds(70) // then the numbers of bins
      .value((d: number) => d)(props.data);

    //@ts-ignore
    const scaleX = d3.scaleLinear().domain([props.x_min, props.x_max]).range([0, width]);

    let scaleY = d3.scaleLinear()
      //@ts-ignore
      .range([height, 0]).domain([0, d3.max(bins, function(d) { return d.length; })]);


    if (axisBottomRef.current) {
      d3.select(axisBottomRef.current).selectAll("*").remove();
      d3.select(axisBottomRef.current).call(d3.axisBottom(scaleX));
      d3.select(axisBottomRef.current).append("text")
        .attr("x", width / 2)
        .attr("y", margin.bottom - 5)
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold')
        .attr('font-size', '1.5em')
        .attr("fill", "currentColor")
        .text(props.labels.x);
    }

    if (axisLeftRef.current) {
      d3.select(axisLeftRef.current).selectAll("*").remove();
      d3.select(axisLeftRef.current).call(d3.axisLeft(scaleY));
      d3.select(axisLeftRef.current).append("text")
        .attr("x", -height / 2)
        .attr("y", -margin.left + 15)
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold')
        .attr('font-size', '1.5em')
        .attr("transform", "rotate(-90)")
        .attr("fill", "currentColor")
        .text(props.labels.y);
    }

    if (chartCanvas.current) {
      d3.select(chartCanvas.current).selectAll("*").remove();
      // set the parameters for the histogram
      // And apply this function to data to get the bins
      // append the bar rectangles to the svg element
      for (let bin of bins) {
        d3.select(chartCanvas.current)
          .append("rect")
          //@ts-ignore
          .attr("x", scaleX(bin.x0) + 1)
          //@ts-ignore
          .attr("width", scaleX(bin.x1) - scaleX(bin.x0) - 1)
          .attr("y", scaleY(bin.length))
          .attr("fill", props.color)
          .attr("height", scaleY(0) - scaleY(bin.length));
      }

    }

  }, [props.labels, props.data, props.legend]);

  return (
    <svg
      style={{
        maxHeight: "60vh",
        maxWidth: "100%"
      }}
      viewBox={`0 0 ${original_width} ${original_height}`}
      id={props.id}
    >
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        <g key="axisBottom" ref={axisBottomRef} transform={`translate(0, ${height})`} />
        <g key="axisLeft" ref={axisLeftRef} />
        <g key="chartCanvas" ref={chartCanvas} />
      </g>
    </svg>
  );
}

